/**
 * Connectors API service.
 *
 * Thin wrapper over the `/api/connectors/*` REST surface. Consumed by
 * TanStack Query hooks in `hooks/data/useConnectors.ts`.
 *
 * The OAuth start endpoint is `POST /connectors/start` (XHR with
 * Authorization + workspace headers); the response carries the
 * gateway's redirect URL which the caller hands to
 * `window.location.href`. Browser-initiated GETs would lose the
 * Firebase JWT and workspace header.
 */

import type { ConnectionSummary, ToolkitCatalogEntry } from '@bower/shared';
import { apiService } from './api.service';

class ConnectorsService {
  async listToolkits() {
    return apiService.get<{ items: ToolkitCatalogEntry[] }>('/connectors/toolkits');
  }

  async listConnections(workspaceId?: string) {
    return apiService.get<ConnectionSummary[]>(
      '/connectors/connections',
      workspaceId ? { headers: { 'x-workspace-id': workspaceId } } : undefined,
    );
  }

  async disconnect(id: string) {
    return apiService.delete<{ id: string }>(`/connectors/connections/${id}`);
  }

  async startOAuth(
    toolkitKey: string,
    returnTo?: '/dashboard' | '/settings?tab=connectors' | '/onboarding',
  ) {
    return apiService.post<{ redirectUrl: string; connectionId: string }>('/connectors/start', {
      toolkit: toolkitKey,
      ...(returnTo ? { returnTo } : {}),
    });
  }
}

export const connectorsService = new ConnectorsService();
